import type { Alignment } from "@platejs/basic-styles";
import { TextAlignPlugin } from "@platejs/basic-styles/react";
import {
  AlignCenter,
  AlignJustify,
  AlignLeft,
  AlignRight,
  ChevronDown,
} from "lucide-react";
import { useEditorPlugin, useSelectionFragmentProp } from "platejs/react";
import { memo, useMemo, useState } from "react";
import { RedoButton } from "../../arbiter-components/buttons/redo-button";
import {
  RedoCommandMenu,
  type RedoCommandMenuItem,
} from "../../arbiter-components/command-menu/redo-command-menu";
import {
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "../ui/dropdown-menu";
import { useToolbarOverflow } from "./toolbar-overflow";

const ALIGN_ITEMS = [
  { Icon: AlignLeft, value: "left", text: "Align left" },
  { Icon: AlignCenter, value: "center", text: "Align center" },
  { Icon: AlignRight, value: "right", text: "Align right" },
  { Icon: AlignJustify, value: "justify", text: "Justify" },
] as const;

export const AlignToolbarButton = memo(function AlignToolbarButton() {
  const { editor, tf } = useEditorPlugin(TextAlignPlugin);
  const value =
    useSelectionFragmentProp({
      defaultValue: "start",
      getProp: (node) => node.align as string,
    }) ?? "left";

  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null);

  const IconValue =
    ALIGN_ITEMS.find((item) => item.value === value)?.Icon ?? AlignLeft;

  const items: RedoCommandMenuItem[] = useMemo(
    () =>
      ALIGN_ITEMS.map(({ Icon, value: itemValue, text }) => ({
        Icon,
        text,
        onClick: () => {
          tf.textAlign.setNodes(itemValue as Alignment);
          editor.tf.focus();
        },
      })),
    [editor, tf],
  );

  const { isOverflow } = useToolbarOverflow();

  if (isOverflow) {
    return (
      <DropdownMenuSub>
        <DropdownMenuSubTrigger>
          <AlignLeft />
          Align text
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent>
          {ALIGN_ITEMS.map(({ Icon, value: itemValue, text }) => (
            <DropdownMenuItem
              key={itemValue}
              onSelect={() => {
                tf.textAlign.setNodes(itemValue as Alignment);
                editor.tf.focus();
              }}
            >
              <Icon />
              {text}
            </DropdownMenuItem>
          ))}
        </DropdownMenuSubContent>
      </DropdownMenuSub>
    );
  }

  return (
    <>
      <RedoButton
        hierarchy="tertiary"
        IconLeading={IconValue}
        IconTrailing={ChevronDown}
        onClick={() => setOpen((prev) => !prev)}
        ref={setAnchor}
        size="md"
        text=""
      />
      <RedoCommandMenu
        anchor={anchor}
        items={items}
        open={open}
        placement="bottom-start"
        setOpen={setOpen}
      />
    </>
  );
});
